import getElementBounds from './getElementBounds'

/**
 * Monitors and stores a DOM element's position relative to the document.
 * Executes a callback if the position changed.
 *
 * Useful for React portals, to track an element's original parent to position
 * the portal accordingly, to look as if it stays put.
 *
 * Example:
 *
 *     this.watcher = new BoundsWatcher()
 *
 *     // React way - but note, when scrolling for example this will call render() at 60fps
 *     this.watcher.start( this.myRef.current, ( refX, refY ) => {
 *         this.setState({ refX, refY })
 *     })
 *
 *     // More CPU-friendly, inline styles, avoiding render()
 *     this.watcher.start( this.myRef.current, ( x, y ) => {
 *         setStylePrefixed( this.myRef.current , { transform: `translate3d(${x}px,${y}px,0px)` })
 *     })
 *
 *     // Eventually stop watching when the portal closes
 *     this.watcher.stop()
 *
 *
 *
 * @TODO Possible enhancements
 *  Track multiple elements
 *  Throttling if bounds don't change for a while
 *
 */
export default class BoundsWatcher {
  constructor() {
    this.el = null
    this.callbackChangePosition = null
    this.callbackChangeSize = null
    this.isActive = false
    this.bounds = { x: -1, y: -1, width: 0, height: 0 }
    this.positionStart = { x: -1, y: -1 }
  }

  /**
   * Starts watching.
   * Callback is invoked immediately once.
   *
   * @TODO If needed immediate call or not could be an optional parameter:
   *
   *   immediate
   *     ? this.bounds.x = this.bounds.y = -1
   *     : this.updateBounds( el, position )
   *
   *
   * @param {Element} element - Element to monitor
   * @param {Function} onChangePosition - Callback when position changes
   */
  start(element, onChangePosition, onChangeSize) {
    if (!this.isValidElement(element)) {
      console.error('BoundsWatcher invalid element:', element)
      return
    }
    if (!onChangePosition || !onChangeSize) {
      console.error('BoundsWatcher invalid callbacks:', onChangePosition, onChangeSize)
      return
    }

    this.el = element
    this.callbackChangePosition = onChangePosition
    this.callbackChangeSize = onChangeSize

    this.bounds.x = this.bounds.y = -1
    this.bounds.width = this.bounds.height = 0

    this.positionStart = getElementBounds(this.el, true, this.positionStart)

    this.isActive = true
    this.update()

    return this
  }

  stop() {
    this.isActive = false
    this.el = null
    this.callbackChangePosition = null
    this.callbackChangeSize = null
  }

  update = () => {
    if (this.isActive) {
      const { x, y, width, height } = this.bounds

      this.bounds = getElementBounds(this.el, true, this.bounds)

      if (x !== this.bounds.x || y !== this.bounds.y) {
        const dX = this.bounds.x - this.positionStart.x
        const dY = this.bounds.y - this.positionStart.y

        this.callbackChangePosition(this.bounds.x, this.bounds.y, dX, dY)
      }

      if (width !== this.bounds.width || height !== this.bounds.height) {
        this.callbackChangeSize(this.bounds.width, this.bounds.height)
      }

      window.requestAnimationFrame(this.update)
    }
  }

  isValidElement(el) {
    return (el && el.getBoundingClientRect)
  }
}
